import moment from 'moment'
import { ISessionItemModel } from 'models/block.model'

export const DEFAULT_SESSION_DURATION = 60
export const SESSION_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/
export const SESSION_TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/

export const formatSessionDate = (date: Date | null): string => {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const parseSessionDate = (value: string): Date | null => {
  if (!SESSION_DATE_REGEX.test(value)) return null
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }
  return date
}

export const isSessionStartDateNotInPast = (value: string): boolean => {
  const date = parseSessionDate(value)
  if (!date) return false

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  date.setHours(0, 0, 0, 0)
  return date >= today
}

export const parseSessionTime = (value: string): moment.Moment | null => {
  if (!SESSION_TIME_REGEX.test(value)) return null
  const parsed = moment(value, 'HH:mm', true)
  return parsed.isValid() ? parsed : null
}

export const formatSessionTime = (time: moment.Moment | null): string => {
  if (!time || !time.isValid()) return ''
  return time.format('HH:mm')
}

export const buildEmptySession = (position: number): ISessionItemModel => ({
  name: '',
  description: '',
  startDate: '',
  startTime: '',
  meetingUrl: '',
  duration: DEFAULT_SESSION_DURATION,
  position,
})

export const normalizeSessions = (
  raw: any[] | undefined
): ISessionItemModel[] => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [buildEmptySession(0)]
  }
  return raw.map((item, index) => ({
    name: item.name || '',
    description: item.description || '',
    startDate: item.startDate || item.start_date || '',
    startTime: item.startTime || item.start_time || '',
    meetingUrl: item.meetingUrl || item.meeting_url || '',
    duration: Number(item.duration ?? DEFAULT_SESSION_DURATION),
    position: item.position ?? index,
  }))
}

export type SessionFormBaseline = {
  name: string
  description: string
  sessions: ISessionItemModel[]
}

const cloneSessions = (sessions: ISessionItemModel[]): ISessionItemModel[] =>
  sessions.map((session) => ({ ...session }))

export const createSessionFormBaseline = (
  name: string,
  description: string,
  sessions: ISessionItemModel[]
): SessionFormBaseline => ({
  name,
  description,
  sessions: cloneSessions(sessions),
})

const sessionFieldSnapshot = (session: ISessionItemModel) => ({
  name: session.name,
  description: session.description ?? '',
  startDate: session.startDate,
  startTime: session.startTime,
  meetingUrl: session.meetingUrl,
  duration: Number(session.duration),
  position: session.position,
})

export const isSessionBlockDirty = (
  name: string,
  description: string,
  sessions: ISessionItemModel[],
  baseline: SessionFormBaseline | null
): boolean => {
  if (!baseline) return true

  if (name !== baseline.name || description !== baseline.description) {
    return true
  }

  if (sessions.length !== baseline.sessions.length) return true

  return sessions.some(
    (session, index) =>
      JSON.stringify(sessionFieldSnapshot(session)) !==
      JSON.stringify(sessionFieldSnapshot(baseline.sessions[index]))
  )
}

const findBaselineSessionForCurrent = (
  session: ISessionItemModel,
  baselineSessions: ISessionItemModel[],
  consumedIndexes: Set<number>
): ISessionItemModel | undefined => {
  const exactIndex = baselineSessions.findIndex(
    (baseline, index) =>
      !consumedIndexes.has(index) &&
      baseline.startDate === session.startDate &&
      baseline.startTime === session.startTime &&
      baseline.meetingUrl === session.meetingUrl
  )
  if (exactIndex >= 0) {
    consumedIndexes.add(exactIndex)
    return baselineSessions[exactIndex]
  }

  const byTimeAndUrl = baselineSessions.findIndex(
    (baseline, index) =>
      !consumedIndexes.has(index) &&
      baseline.startTime === session.startTime &&
      baseline.meetingUrl === session.meetingUrl
  )
  if (byTimeAndUrl >= 0) {
    consumedIndexes.add(byTimeAndUrl)
    return baselineSessions[byTimeAndUrl]
  }

  return undefined
}

export const sessionStartDateRequiresFuture = (
  session: ISessionItemModel,
  baselineSessions: ISessionItemModel[] | undefined,
  consumedBaselineIndexes: Set<number>
): boolean => {
  if (!baselineSessions?.length) return true

  const matched = findBaselineSessionForCurrent(
    session,
    baselineSessions,
    consumedBaselineIndexes
  )
  if (!matched) return true

  return session.startDate !== matched.startDate
}

export type SessionAcceptDisabledReasonKey =
  | 'noChanges'
  | 'blockNameRequired'
  | 'noSessions'
  | 'sessionDateFormat'
  | 'sessionDateInvalid'
  | 'sessionDateInPast'
  | 'sessionTimeFormat'
  | 'sessionTimeInvalid'
  | 'sessionUrlInvalid'
  | 'sessionDurationInvalid'

export type SessionAcceptDisabledReason = {
  key: SessionAcceptDisabledReasonKey
  sessionNumber?: number
}

export type SessionFormValidationContext = {
  isEditingExistingBlock: boolean
  baseline: SessionFormBaseline | null
}

export const getSessionAcceptDisabledReason = (
  name: string,
  description: string,
  sessions: ISessionItemModel[],
  context: SessionFormValidationContext
): SessionAcceptDisabledReason | null => {
  const { isEditingExistingBlock, baseline } = context

  if (
    isEditingExistingBlock &&
    baseline &&
    !isSessionBlockDirty(name, description, sessions, baseline)
  ) {
    return { key: 'noChanges' }
  }

  if (!name.trim()) {
    return { key: 'blockNameRequired' }
  }

  if (!sessions.length) {
    return { key: 'noSessions' }
  }

  const consumedBaselineIndexes = new Set<number>()
  const baselineSessions = isEditingExistingBlock
    ? baseline?.sessions
    : undefined

  for (let index = 0; index < sessions.length; index += 1) {
    const session = sessions[index]
    const sessionNumber = index + 1
    const startDate = session.startDate || ''

    if (!SESSION_DATE_REGEX.test(startDate)) {
      return { key: 'sessionDateFormat', sessionNumber }
    }

    if (parseSessionDate(startDate) == null) {
      return { key: 'sessionDateInvalid', sessionNumber }
    }

    const requiresFutureDate =
      !isEditingExistingBlock ||
      sessionStartDateRequiresFuture(
        session,
        baselineSessions,
        consumedBaselineIndexes
      )

    if (requiresFutureDate && !isSessionStartDateNotInPast(startDate)) {
      return { key: 'sessionDateInPast', sessionNumber }
    }

    const startTime = session.startTime || ''
    if (!SESSION_TIME_REGEX.test(startTime)) {
      return { key: 'sessionTimeFormat', sessionNumber }
    }

    if (parseSessionTime(startTime) == null) {
      return { key: 'sessionTimeInvalid', sessionNumber }
    }

    if (!/^https?:\/\//i.test(session.meetingUrl || '')) {
      return { key: 'sessionUrlInvalid', sessionNumber }
    }

    if (Number(session.duration) <= 0) {
      return { key: 'sessionDurationInvalid', sessionNumber }
    }
  }

  return null
}

export const isSessionFormValid = (
  name: string,
  sessions: ISessionItemModel[],
  context: SessionFormValidationContext = {
    isEditingExistingBlock: false,
    baseline: null,
  },
  description = ''
): boolean =>
  getSessionAcceptDisabledReason(name, description, sessions, context) == null
